import { ActionIcon, MantineSize, Popover, TextInput } from '@mantine/core';
import { PrimitiveTypeIcon, TooltipWhite } from '@root/Design/Primitives';
import { useToggle } from '@root/Services/EventEmitter';
import { FieldInfo, primitiveIcons, PrimitiveType, SchemaService, TypeInfo } from '@root/Services/QueryExpr';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'tabler-icons-react';
import { VisibleSpaces } from '../Text/VisibleSpaces';
import { UdIcon } from './IconPicker';
import { Picker } from './Picker';

type IFieldPickerDropdownProps = {
    value: FieldInfo | undefined;
    onChange: (value?: FieldInfo) => void;
    label?: ReactNode;
    description?: ReactNode;
    size?: MantineSize;
    placeholder?: string;
} & Omit<FieldPickerProps, 'onChange' | 'selections' | 'mode'>;
export function FieldPickerDropdown(props: IFieldPickerDropdownProps) {
    const { description, label, placeholder, size } = props;
    const [opened, { close, toggle }] = useToggle(false);
    const selections = useMemo(() => (props.value ? [props.value] : []), [props.value]);
    const onChange = useCallback(
        (selections: FieldInfo[]) => {
            props.onChange(selections[0]);
            close();
        },
        [props.onChange, close]
    );

    return (
        <Popover withinPortal opened={opened} onClose={close} shadow="md" position="bottom" offset={0} withArrow>
            <Popover.Target>
                <TextInput
                    value={props.value?.name ?? ''}
                    placeholder={placeholder}
                    onClick={toggle}
                    readOnly
                    label={label}
                    size={size}
                    description={description}
                    rightSection={
                        <ActionIcon variant={opened ? 'default' : undefined} color={opened ? 'primary' : undefined} tabIndex={-1} onClick={toggle}>
                            {opened ? <ChevronUp strokeWidth={3} size={20} /> : <ChevronDown strokeWidth={1} size={18} />}
                        </ActionIcon>
                    }
                    style={{ cursor: 'pointer' }}
                />
            </Popover.Target>
            <Popover.Dropdown p={0}>
                <FieldPicker {...props} onChange={onChange} selections={selections} mode="single" />
            </Popover.Dropdown>
        </Popover>
    );
}

export function FieldPickerTooltip({
    children,
    helpText,
    position,
}: {
    children: ReactNode;
    helpText?: ReactNode;
    position?: 'bottom' | 'top' | 'left' | 'right';
}) {
    return !helpText ? (
        <>{children}</>
    ) : (
        <TooltipWhite openDelay={300} withinPortal multiline position={position ?? 'left'} width={300} label={helpText}>
            <span>{children}</span>
        </TooltipWhite>
    );
}

export function FieldIcon({ field }: { field: FieldInfo }) {
    const icon = field.supplementalInfo?.Icon;
    const name = icon?.Name ? icon.Name : !field.isPrimitive ? null : primitiveIcons[field.field.TypeName as PrimitiveType];
    const color = icon?.Color || undefined;
    return !name ? (
        <></>
    ) : !icon?.Name ? (
        <PrimitiveTypeIcon className={name} color={color}></PrimitiveTypeIcon>
    ) : (
        <UdIcon withBackground icon={icon} />
    );
}

export function FieldPickerItem(props: { item: FieldPickerItem; tooltipPosition?: 'top' | 'bottom' | 'left' | 'right' }) {
    const { item, tooltipPosition } = props;
    let result = <>{item.name}</>;
    const field = 'field' in item ? item : undefined;
    const pathWithRoot = field?.pathWithRoot;

    if (pathWithRoot?.startsWith('Tags.') || pathWithRoot?.startsWith('resourceTags.') || field?.flags.has('tag')) {
        result = <VisibleSpaces value={item.name} />;
    }

    if (field) {
        result = (
            <>
                <FieldIcon field={field} /> {result}
            </>
        );
    }

    if (field?.helpText) {
        result = (
            <FieldPickerTooltip helpText={field.helpText} position={tooltipPosition}>
                {result}
            </FieldPickerTooltip>
        );
    }

    return result;
}

export function FieldPicker({ schema, types, mode, selections, onChange, width = 400, ...props }: FieldPickerProps) {
    let items = useMemo(() => {
        let result: FieldPickerItem[] = schema.rootTypeInfo.length > 1 ? schema.rootTypeInfo : schema.rootTypeInfo[0]?.children ?? [];
        if (props.transformItems) {
            result = props.transformItems(result);
        }
        return result;
    }, [schema, props.transformItems]);
    const typeLookup = useMemo(() => (types ? new Set<string>(types) : undefined), [types]);
    const isSelectable = useMemo(() => {
        return props.isSelectable ? props.isSelectable : (o: FieldPickerItem) => o instanceof FieldInfo && o.isPrimitive;
    }, [props.isSelectable]);
    const minimizeHeight = props.minimizeHeight !== false;
    const height = props.height ?? (minimizeHeight ? 350 : undefined);
    const filter = useCallback(
        (item: FieldPickerItem) => {
            const matchFieldType = !typeLookup || ('isPrimitive' in item && typeLookup.has(item.field.TypeName ?? ''));
            const matchSubfield =
                !typeLookup || !!('isPrimitive' in item && getChildren(item)?.some((c) => typeLookup?.has((c as FieldInfo).typeName ?? '')));
            const matchFilter = !props.schemaFilter || props.schemaFilter(item);
            return (matchFieldType || matchSubfield) && matchFilter;
        },
        [typeLookup, props.schemaFilter]
    );
    const getChildren = useCallback(
        (item: FieldPickerItem) => {
            return props.getChildren ? props.getChildren(item) : item.children;
        },
        [props.getChildren]
    );
    const defaultItemRenderer = useCallback(
        (o: FieldPickerItem): ReactNode => <FieldPickerItem tooltipPosition={props.tooltipPosition} item={o} />,
        []
    );

    const renderFieldName = useCallback(
        (o: FieldPickerItem) => {
            return props.renderItem?.(o, () => defaultItemRenderer(o)) ?? defaultItemRenderer(o);
        },
        [props.renderItem]
    );

    return (
        <Picker
            items={items}
            selections={selections as FieldPickerItem[]}
            onChange={onChange as (selections: FieldPickerItem[]) => void}
            childAccessor={getChildren}
            isSelectable={isSelectable}
            isDisabled={props.isDisabled}
            nameAccessor={(o) => o.name}
            filterPlaceholder="Find field"
            mode={mode}
            width={width}
            minimizeHeight={minimizeHeight}
            height={height}
            renderItem={renderFieldName}
            filter={filter}
            data-atid="field-picker"
        />
    );
}
export type FieldPickerItem = FieldInfo | TypeInfo;
interface FieldPickerProps {
    schema: SchemaService;
    types?: string[];
    mode: 'single' | 'multiple';
    selections: FieldInfo[];
    isSelectable?: (item: FieldPickerItem) => boolean;
    isDisabled?: (item: FieldPickerItem) => boolean;
    onChange: (selections: FieldInfo[]) => void;
    renderItem?: (item: FieldPickerItem, defaultRenderer: () => ReactNode) => ReactNode;
    schemaFilter?: (item: FieldInfo | TypeInfo) => boolean;
    transformItems?: (items: FieldPickerItem[]) => FieldPickerItem[];
    getChildren?: (item: FieldPickerItem) => undefined | FieldPickerItem[];
    width?: string | number;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    minimizeHeight?: boolean;
    height?: number;
    noFilter?: boolean;
}
